/** What each refusal of `POST /inventory/activities/{id}/verify` means, to the person who got it.
 *
 * Its own module so the screen file exports only components, and so a test can read the table
 * without rendering anything.
 */

import type { VerifyRefusalCode } from "../api/verifyStatedBasis";

/** What each coded refusal means, to the person who got it. One home, so two screens cannot
 * describe the same refusal differently (design rule 7).
 *
 * `kind` is the line this card lives on: `separation` is about *you and this figure*, `figure` is
 * about the figure's state, `flags` is about work still open on it. A test fails for each pair. */
export const VERIFY_REFUSALS: Record<
  VerifyRefusalCode,
  { kind: "separation" | "figure" | "flags"; head: string; body: string }
> = {
  attester_cannot_verify: {
    kind: "separation",
    head: "You stated the basis for this figure, so someone else has to accept it.",
    body:
      "This is separation of duties, not a permission: it applies to you whatever your role, " +
      "and an Admin or Approver is refused for their own figure in exactly the same way. Ask " +
      "another Admin or Approver to accept it. If you are the only Admin or Approver in this " +
      "workspace, inviting a second one is what makes acceptance possible.",
  },
  blocking_flag_open: {
    kind: "flags",
    head: "A blocking flag is open on this figure.",
    body: "Resolve it first; an approval of the same record is refused for the same reason.",
  },
  has_a_document: {
    kind: "figure",
    head: "This figure rests on a document.",
    body: "It is accepted by reviewing that document, not here. This accepts only a figure that has no document and states its own basis.",
  },
  no_stated_basis: {
    kind: "figure",
    head: "There is nothing to accept.",
    body: "This figure states no basis and has no document, so no one has said where it came from.",
  },
  superseded: {
    kind: "figure",
    head: "A correction has replaced this figure.",
    body: "It is no longer in force and is in no current total. Accept the figure that replaced it.",
  },
  already_verified: {
    kind: "figure",
    head: "This figure was already accepted.",
    body: "The first acceptance stands and nothing was recorded a second time. Pressing again would only have re-attributed it.",
  },
  no_such_record: {
    kind: "figure",
    head: "There is no such figure in this workspace.",
    body: "It may have been removed, or the link may belong to a different workspace.",
  },
};
